import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppStore } from '../stores/appStore';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { buildWebSocketUrl } from '../utils/serverOrigin';

/* ============================================================
   PCM utilities (ported from public/voice/app.js)
   ============================================================ */

function downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (outputRate === inputRate) return buffer;
  if (outputRate > inputRate) throw new Error('Output rate must be <= input rate');

  const ratio = inputRate / outputRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);

  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;

    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }

    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
}

function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i += 1) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

function int16ToBase64(int16Array: Int16Array): string {
  const bytes = new Uint8Array(int16Array.buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function base64ToInt16(base64: string): Int16Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

/* ============================================================
   useVoice hook
   ============================================================ */

export interface TaskStatus {
  taskId: string;
  label: string;
  phase: 'thinking' | 'done' | 'error';
  text: string;
  dismissing?: boolean;
}

export interface UseVoiceReturn {
  voiceConnected: boolean;
  voiceConnecting: boolean;
  isRecording: boolean;
  voiceError: string | null;
  liveTranscript: string;
  activeTasks: TaskStatus[];

  connectVoice: () => void;
  disconnectVoice: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

export function useVoice(): UseVoiceReturn {
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [activeTasks, setActiveTasks] = useState<TaskStatus[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const silentGainRef = useRef<GainNode | null>(null);
  const mediaSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const nextPlaybackTimeRef = useRef(0);
  const isRecordingRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transcriptAccumRef = useRef('');
  const intentionalCloseRef = useRef(false);

  // Keep isRecordingRef in sync
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  const sendToServer = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  }, []);

  const playPcmChunk = useCallback((base64Pcm16: string) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
    }
    const ctx = audioContextRef.current;

    const int16 = base64ToInt16(base64Pcm16);
    if (!int16.length) return;

    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i += 1) {
      float32[i] = int16[i] / 32768;
    }

    const audioBuffer = ctx.createBuffer(1, float32.length, 24000);
    audioBuffer.copyToChannel(float32, 0);

    const src = ctx.createBufferSource();
    src.buffer = audioBuffer;
    src.connect(ctx.destination);

    if (nextPlaybackTimeRef.current < ctx.currentTime) {
      nextPlaybackTimeRef.current = ctx.currentTime;
    }

    src.start(nextPlaybackTimeRef.current);
    nextPlaybackTimeRef.current += audioBuffer.duration;
  }, []);

  const connectVoice = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;

    intentionalCloseRef.current = false;
    setVoiceConnecting(true);
    setVoiceError(null);

    const gatewayUrl = useAppStore.getState().gatewayUrl;
    const gatewayToken = useAppStore.getState().gatewayToken;
    const wsUrl = buildWebSocketUrl('/voice/ws', gatewayUrl);
    wsUrl.searchParams.set('token', gatewayToken);

    const ws = new WebSocket(wsUrl.toString());
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      setVoiceConnecting(false);
    });

    ws.addEventListener('message', (event) => {
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }

      if (msg.type === 'session.ready') {
        setVoiceConnected(true);
        setVoiceConnecting(false);
        setVoiceError(null);
        return;
      }

      if (msg.type === 'audio.delta' && msg.data) {
        playPcmChunk(msg.data as string);
        return;
      }

      if (msg.type === 'transcript.delta') {
        transcriptAccumRef.current += (msg.text as string) || '';
        setLiveTranscript(transcriptAccumRef.current);
        return;
      }

      if (msg.type === 'response.done') {
        const transcript = ((msg.transcript as string) || transcriptAccumRef.current || '').trim();

        const activeSessionKey = useAppStore.getState().activeSessionKey;
        if (activeSessionKey && transcript) {
          useAppStore.getState().addMessage(activeSessionKey, {
            role: 'assistant',
            content: transcript,
            timestamp: new Date().toISOString(),
          });
        }

        transcriptAccumRef.current = '';
        setLiveTranscript('');
        nextPlaybackTimeRef.current = 0;
        return;
      }

      if (msg.type === 'error') {
        console.error('[useVoice] Voice error event:', msg.message);
        setVoiceError(toFriendlyErrorMessage(msg.message, 'voice'));
        return;
      }

      if (msg.type === 'task.start') {
        const taskId = msg.taskId as string;
        const label = msg.label as string;
        setActiveTasks((prev) => [...prev, { taskId, label, phase: 'thinking', text: '' }]);
        return;
      }

      if (msg.type === 'task.thinking') {
        const taskId = msg.taskId as string;
        const text = msg.text as string;
        setActiveTasks((prev) =>
          prev.map((t) => (t.taskId === taskId ? { ...t, text } : t))
        );
        return;
      }

      if (msg.type === 'task.done' || msg.type === 'task.error') {
        const taskId = msg.taskId as string;
        const isError = msg.type === 'task.error';
        const phase = isError ? 'error' : 'done';
        const text = isError
          ? toFriendlyErrorMessage(msg.error, 'voice-task')
          : (msg.result as string);

        if (isError) {
          console.error('[useVoice] Voice task failed:', msg.error);
        }

        setActiveTasks((prev) =>
          prev.map((t) => (t.taskId === taskId ? { ...t, phase, text } : t))
        );

        setTimeout(() => {
          setActiveTasks((prev) =>
            prev.map((t) => (t.taskId === taskId ? { ...t, dismissing: true } : t))
          );
          setTimeout(() => {
            setActiveTasks((prev) => prev.filter((t) => t.taskId !== taskId));
          }, 500);
        }, 5000);
        return;
      }
    });

    ws.addEventListener('close', () => {
      setVoiceConnected(false);
      setVoiceConnecting(false);

      if (!intentionalCloseRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          connectVoice();
        }, 1500);
      }
    });

    ws.addEventListener('error', (event) => {
      console.error('[useVoice] Voice socket error:', event);
      setVoiceError(toFriendlyErrorMessage('Voice socket error', 'voice'));
      setVoiceConnecting(false);
    });
  }, [playPcmChunk]);

  const disconnectVoice = useCallback(() => {
    intentionalCloseRef.current = true;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setVoiceConnected(false);
    setVoiceConnecting(false);
    setIsRecording(false);
    setLiveTranscript('');
    transcriptAccumRef.current = '';
    setActiveTasks([]);
  }, []);

  const ensureCapturePipeline = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }

    if (processorRef.current) return;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    const ctx = audioContextRef.current;
    const source = ctx.createMediaStreamSource(stream);
    mediaSourceRef.current = source;

    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    const gain = ctx.createGain();
    gain.gain.value = 0;
    silentGainRef.current = gain;

    processor.onaudioprocess = (ev) => {
      if (!isRecordingRef.current) return;

      const input = ev.inputBuffer.getChannelData(0);
      const downsampled = downsampleBuffer(input, ctx.sampleRate, 24000);
      const pcm16 = floatTo16BitPCM(downsampled);

      sendToServer({
        type: 'audio.append',
        data: int16ToBase64(pcm16),
      });
    };

    source.connect(processor);
    processor.connect(gain);
    gain.connect(ctx.destination);
  }, [sendToServer]);

  const startRecording = useCallback(async () => {
    if (isRecordingRef.current) return;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setVoiceError(toFriendlyErrorMessage('Voice not connected', 'voice'));
      return;
    }

    try {
      await ensureCapturePipeline();
      await audioContextRef.current?.resume();
    } catch (err: unknown) {
      console.error('[useVoice] Microphone error:', err);
      setVoiceError(toFriendlyErrorMessage(err, 'voice-mic'));
      return;
    }

    setIsRecording(true);

    const activeSessionKey = useAppStore.getState().activeSessionKey;
    if (activeSessionKey) {
      useAppStore.getState().addMessage(activeSessionKey, {
        role: 'user',
        content: '(voice)',
        timestamp: new Date().toISOString(),
      });
    }
  }, [ensureCapturePipeline]);

  const stopRecording = useCallback(() => {
    if (!isRecordingRef.current) return;

    setIsRecording(false);
    sendToServer({ type: 'audio.commit' });
  }, [sendToServer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      intentionalCloseRef.current = true;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close();
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }

      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
    };
  }, []);

  return {
    voiceConnected,
    voiceConnecting,
    isRecording,
    voiceError,
    liveTranscript,
    activeTasks,

    connectVoice,
    disconnectVoice,
    startRecording,
    stopRecording,
  };
}
